/**
 * Data Mapper - Standardisation des noms de champs
 *
 * Ce module centralise la normalisation des données pour éliminer les incohérences
 * entre les différents noms de champs utilisés dans l'application.
 *
 * Standards adoptés :
 * - price_per_day (pas price)
 * - vehicle_id (pas car_id)
 * - owner_id (pas host_id)
 * - renter_id (pas user_id pour les bookings)
 * - total_price (pas totalAmount ou total_amount)
 */

import type { Vehicle } from "@/types/vehicle";
import type { Booking } from "@/types/booking";

/**
 * Interface pour les données de véhicule brutes (avant normalisation)
 */
export interface RawVehicle {
  id: string;
  owner_id?: string;
  host_id?: string;
  price_per_day?: number;
  price?: number;
  car_id?: string;
  vehicle_id?: string;
  [key: string]: unknown;
}

/**
 * Interface pour les données de booking brutes (avant normalisation)
 */
export interface RawBooking {
  id: string;
  vehicle_id?: string;
  car_id?: string;
  renter_id?: string;
  user_id?: string;
  owner_id?: string;
  host_id?: string;
  total_price?: number;
  totalAmount?: number;
  total_amount?: number;
  [key: string]: unknown;
}

/**
 * Normalise un véhicule en standardisant les noms de champs
 *
 * @param vehicle - Données brutes du véhicule
 * @returns Véhicule normalisé avec les champs standards
 */
export function normalizeVehicle(vehicle: RawVehicle | null | undefined): Vehicle | null {
  if (!vehicle) return null;

  // Extraire l'ID (peut être dans id, vehicle_id, ou car_id)
  const id = vehicle.id || vehicle.vehicle_id || vehicle.car_id || "";

  // Extraire owner_id (peut être owner_id ou host_id)
  const owner_id = vehicle.owner_id || vehicle.host_id || "";

  // Extraire price_per_day (peut être price_per_day ou price)
  const price_per_day = vehicle.price_per_day ?? vehicle.price ?? 0;

  // Construire l'objet normalisé
  const normalized: Vehicle = {
    id,
    owner_id,
    price_per_day,
    make: (vehicle.make || vehicle.brand || "") as string,
    model: (vehicle.model || "") as string,
    year: (vehicle.year || 0) as number,
    location: (vehicle.location || "") as string,
    description: vehicle.description as string | undefined,
    images: Array.isArray(vehicle.images)
      ? vehicle.images
      : vehicle.image_url
        ? [vehicle.image_url]
        : [],
    status: (vehicle.status || "available") as Vehicle["status"],
    publication_status: vehicle.publication_status as Vehicle["publication_status"],
    fuel_type: vehicle.fuel_type as Vehicle["fuel_type"],
    luggage: vehicle.luggage as number | undefined,
    mileage: vehicle.mileage as number | undefined,
    color: vehicle.color as string | undefined,
    transmission: vehicle.transmission as Vehicle["transmission"],
    seats: vehicle.seats as number | undefined,
    features: (() => {
      // Gérer différents formats de features (array, JSONB, null, undefined)
      if (!vehicle.features) return [];
      if (Array.isArray(vehicle.features)) {
        // S'assurer que tous les éléments sont des strings
        return vehicle.features.filter((f): f is string => typeof f === "string");
      }
      // Si c'est un objet JSONB, essayer de le convertir
      if (typeof vehicle.features === "object" && "length" in vehicle.features) {
        try {
          const arr = Array.from(vehicle.features as any);
          return arr.filter((f): f is string => typeof f === "string");
        } catch {
          return [];
        }
      }
      return [];
    })(),
    rating: vehicle.rating as number | undefined,
    reviews_count: vehicle.reviews_count as number | undefined,
    category: vehicle.category as Vehicle["category"],
    latitude: vehicle.latitude as number | undefined,
    longitude: vehicle.longitude as number | undefined,
    is_premium: vehicle.is_premium as boolean | undefined,
    fuel_policy: vehicle.fuel_policy as string | undefined,
    deductible: vehicle.deductible as number | string | undefined,
    deposit: (vehicle.deposit ?? (vehicle as RawVehicle & { deposit_amount?: number }).deposit_amount) as number | string | undefined,
    delivery_zone: vehicle.delivery_zone as string | undefined,
    pickup_locations: (() => {
      if (!vehicle.pickup_locations) return undefined;
      if (Array.isArray(vehicle.pickup_locations)) {
        return vehicle.pickup_locations as Vehicle["pickup_locations"];
      }
      // Si c'est un JSONB, essayer de le parser
      try {
        const parsed = typeof vehicle.pickup_locations === "string"
          ? JSON.parse(vehicle.pickup_locations)
          : vehicle.pickup_locations;
        return Array.isArray(parsed) ? parsed : undefined;
      } catch {
        return undefined;
      }
    })(),
    return_locations: (() => {
      if (!vehicle.return_locations) return undefined;
      if (Array.isArray(vehicle.return_locations)) {
        return vehicle.return_locations as Vehicle["return_locations"];
      }
      // Si c'est un JSONB, essayer de le parser
      try {
        const parsed = typeof vehicle.return_locations === "string"
          ? JSON.parse(vehicle.return_locations)
          : vehicle.return_locations;
        return Array.isArray(parsed) ? parsed : undefined;
      } catch {
        return undefined;
      }
    })(),
    minimum_rental_days:
      vehicle.minimum_rental_days != null
        ? Number(vehicle.minimum_rental_days)
        : undefined,
    long_term_discounts: (() => {
      const raw = vehicle.long_term_discounts;
      if (!raw) return undefined;
      if (Array.isArray(raw)) {
        return raw
          .filter((t): t is { from_days: number; percent: number } =>
            t != null && typeof (t as any).from_days === "number" && typeof (t as any).percent === "number"
          )
          .map((t) => ({ from_days: (t as any).from_days, percent: Number((t as any).percent) }));
      }
      if (typeof raw === "string") {
        try {
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed.map((t: any) => ({ from_days: Number(t.from_days), percent: Number(t.percent) })) : undefined;
        } catch {
          return undefined;
        }
      }
      return undefined;
    })(),
    created_at: (vehicle.created_at || new Date().toISOString()) as string,
    updated_at: (vehicle.updated_at || new Date().toISOString()) as string,
    availability_confirmed_at: (vehicle.availability_confirmed_at as string | null | undefined) ?? null,
  };

  // Ajouter les alias pour compatibilité (à supprimer progressivement)
  normalized.brand = normalized.make;
  normalized.price = normalized.price_per_day;
  normalized.name = `${normalized.make} ${normalized.model} ${normalized.year}`;
  normalized.image_url = normalized.images[0];

  // Préserver les données owner si présentes (jointure Supabase)
  // Permet d'accéder à owner.verified_host pour les badges
  if (vehicle.owner) {
    (normalized as any).owner = vehicle.owner;
  }

  return normalized;
}

/**
 * Normalise une liste de véhicules
 */
export function normalizeVehicles(vehicles: RawVehicle[] | null | undefined): Vehicle[] {
  if (!vehicles || !Array.isArray(vehicles)) return [];
  return vehicles.map(normalizeVehicle).filter((v): v is Vehicle => v !== null);
}

/**
 * Normalise un booking en standardisant les noms de champs
 *
 * @param booking - Données brutes du booking
 * @returns Booking normalisé avec les champs standards
 */
export function normalizeBooking(booking: RawBooking | null | undefined): Booking | null {
  if (!booking) return null;

  // Extraire vehicle_id (peut être vehicle_id ou car_id)
  const vehicle_id = booking.vehicle_id || booking.car_id || "";

  // Extraire renter_id (peut être renter_id ou user_id)
  const renter_id = booking.renter_id || booking.user_id || "";

  // Extraire owner_id (peut être owner_id ou host_id)
  const owner_id = booking.owner_id || booking.host_id || "";

  // Extraire total_price (peut être total_price, totalAmount, ou total_amount)
  const total_price = booking.total_price ?? booking.totalAmount ?? booking.total_amount ?? 0;

  // Construire l'objet normalisé
  const normalized: Booking = {
    id: booking.id || "",
    vehicle_id,
    renter_id,
    owner_id,
    start_date: (booking.start_date || "") as string,
    end_date: (booking.end_date || "") as string,
    status: (booking.status || "pending") as Booking["status"],
    total_price,
    pickup_location: (booking.pickup_location || "") as string,
    created_at: (booking.created_at || new Date().toISOString()) as string,
    updated_at: (booking.updated_at || new Date().toISOString()) as string,
    check_in_at: (booking.check_in_at || null) as string | null,
    check_out_at: (booking.check_out_at || null) as string | null,
    check_in_out_status: (booking.check_in_out_status || null) as string | null,
  };

  // Ajouter les relations si présentes
  if (booking.vehicle) {
    normalized.vehicle = normalizeVehicle(booking.vehicle as RawVehicle) as Booking["vehicle"];
  }

  if (booking.owner) {
    normalized.owner = booking.owner as Booking["owner"];
  }

  if (booking.renter) {
    normalized.renter = booking.renter as Booking["renter"];
  }

  return normalized;
}

/**
 * Normalise une liste de bookings
 */
export function normalizeBookings(bookings: RawBooking[] | null | undefined): Booking[] {
  if (!bookings || !Array.isArray(bookings)) return [];
  return bookings.map(normalizeBooking).filter((b): b is Booking => b !== null);
}

/**
 * Normalise les données d'un véhicule pour l'affichage
 * Garantit que price_per_day est toujours présent
 */
export function normalizeVehicleForDisplay(vehicle: RawVehicle | null | undefined): {
  id: string;
  price_per_day: number;
  owner_id: string;
  [key: string]: unknown;
} | null {
  if (!vehicle) return null;

  return {
    ...vehicle,
    id: vehicle.id || vehicle.vehicle_id || vehicle.car_id || "",
    owner_id: vehicle.owner_id || vehicle.host_id || "",
    price_per_day: vehicle.price_per_day ?? vehicle.price ?? 0,
    vehicle_id: vehicle.vehicle_id || vehicle.car_id || vehicle.id || "",
  };
}

/**
 * Normalise les données d'un booking pour l'affichage
 * Garantit que total_price, vehicle_id, renter_id, owner_id sont toujours présents
 */
export function normalizeBookingForDisplay(booking: RawBooking | null | undefined): {
  id: string;
  vehicle_id: string;
  renter_id: string;
  owner_id: string;
  total_price: number;
  [key: string]: unknown;
} | null {
  if (!booking) return null;

  return {
    ...booking,
    id: booking.id || "",
    vehicle_id: booking.vehicle_id || booking.car_id || "",
    renter_id: booking.renter_id || booking.user_id || "",
    owner_id: booking.owner_id || booking.host_id || "",
    total_price: booking.total_price ?? booking.totalAmount ?? booking.total_amount ?? 0,
  };
}
